import { useState, useEffect } from "react"
import { Check, X, Loader2, Cloud, CloudOff } from "lucide-react"
import { format } from "date-fns"
import { useTranslation } from "react-i18next"

import { CopyButton } from "@f2e/ui/components/Button"
import { Card, Label, Input, Button } from "@f2e/ui/components/base"

import { PermissionGuard } from "@/components/Auth"
import { EditSaveButtons } from "@/components"
import {
  ClientChannel,
  isWebClientConfig,
  isWidgetClientConfig,
  Workspace,
  Permission,
} from "@/types"
import { useToast, useDebouncedEffect } from "@/hooks"
import {
  FetchStatus,
  useUpdateClientChannelApi,
  useCheckSlugAvailabilityApi,
  clientChannelResponseToClientChannel,
  makeUpdateClientChannelRequest,
} from "@/hooks/api"
import { useStore } from "@/store"
import {
  ClientChannelSlugIssue,
  getClientChannelSlugIssue,
  isReservedClientChannelSlug,
} from "@/constants/clientChannels"
import {
  getUserFacingApiErrorMessage,
  normalizeApiError,
} from "@/lib/apiErrors"

export interface ChannelOverviewProps {
  channel: ClientChannel
  workspace: Workspace
  onChannelUpdated?: (channel: ClientChannel) => void
  onUnsavedChangesChange?: (dirty: boolean) => void
}

export const ChannelOverview = ({
  channel,
  workspace,
  onChannelUpdated,
  onUnsavedChangesChange,
}: ChannelOverviewProps) => {
  const { t } = useTranslation()
  const { successToast, alertToast } = useToast()
  const serverRoot = useStore((state) => state.sysConfig.serverRoot)
  const clientChannelDomain = useStore(
    (state) => state.sysConfig.clientChannelDomain
  )
  const showOauthDevTools =
    import.meta.env.DEV ||
    import.meta.env.VITE_ENABLE_OAUTH_TEST_BUTTON === "true"
  const resolvedClientChannelDomain =
    clientChannelDomain?.trim().replace(/^\.+/, "") || "beta-web.codeer.ai"
  const widgetScriptOrigin =
    resolvedClientChannelDomain === "web.codeer.ai"
      ? "https://app.codeer.ai"
      : resolvedClientChannelDomain === "beta-web.codeer.ai"
        ? "https://beta.codeer.ai"
        : window.location.origin
  const widgetAutoInitSnippet = `<script async src="${widgetScriptOrigin}/web-widget/widget.js?key=${channel.slug}"></script>`
  const widgetManualInitSnippet = `<script>
  window.YourWidget =
    window.YourWidget ||
    function () {
      (window.YourWidget.q = window.YourWidget.q || []).push(arguments)
    }

  window.YourWidget("init", {
    widgetKey: "${channel.slug}",
    appearance: {
      title: "Support",
      position: "bottom-left",
      launcher: {
        label: "Need help?",
      },
    },
  })
</script>
<script async src="${widgetScriptOrigin}/web-widget/widget.js"></script>`

  const [isEditingBasicInfo, setIsEditingBasicInfo] = useState(false)
  const [editedName, setEditedName] = useState(channel.name)
  const [editedSlug, setEditedSlug] = useState(channel.slug)
  const [isCheckingSlug, setIsCheckingSlug] = useState(false)
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null)
  const [slugIssue, setSlugIssue] = useState<ClientChannelSlugIssue | null>(
    null
  )
  const hasUnsavedBasicInfoChanges =
    editedName.trim() !== channel.name || editedSlug.trim() !== channel.slug

  const getSlugErrorMessage = (issue: ClientChannelSlugIssue): string => {
    return issue === "reserved"
      ? t("workspace.channels.overview.basicInfo.slugReserved")
      : t("workspace.channels.overview.basicInfo.slugTaken")
  }

  // APIs
  const {
    data: updateChannelRespData,
    error: updateChannelRespError,
    status: updateChannelRespStatus,
    fetchFn: updateChannel,
  } = useUpdateClientChannelApi(
    workspace.organizationId || "",
    workspace.id,
    channel.id
  )
  const {
    data: checkSlugRespData,
    status: checkSlugRespStatus,
    fetchFn: checkSlug,
  } = useCheckSlugAvailabilityApi(workspace.organizationId || "", workspace.id)

  useEffect(() => {
    setEditedName(channel.name)
    setEditedSlug(channel.slug)
    setSlugAvailable(null)
    setSlugIssue(null)
  }, [channel])

  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedBasicInfoChanges)
  }, [hasUnsavedBasicInfoChanges, onUnsavedChangesChange])

  // Debounce slug check when user types
  useDebouncedEffect({
    callback: () => {
      checkSlug({
        searchParams: new URLSearchParams({
          slug: editedSlug,
        }),
      })
    },
    delay: 500,
    condition: () => {
      return (
        !!editedSlug &&
        editedSlug !== channel.slug &&
        isEditingBasicInfo &&
        !isReservedClientChannelSlug(editedSlug)
      )
    },
    onDebounceStart: () => setIsCheckingSlug(true),
    onSkip: () => {
      setIsCheckingSlug(false)
      if (!editedSlug || editedSlug === channel.slug || !isEditingBasicInfo) {
        setSlugAvailable(null)
        setSlugIssue(null)
        return
      }

      if (isReservedClientChannelSlug(editedSlug)) {
        setSlugAvailable(null)
        setSlugIssue("reserved")
        return
      }

      setSlugAvailable(null)
      setSlugIssue(null)
    },
    deps: [editedSlug, channel.slug, isEditingBasicInfo, checkSlug],
  })

  // Handle check slug response
  useEffect(() => {
    if (checkSlugRespStatus === FetchStatus.Success && checkSlugRespData) {
      const isAvailable = checkSlugRespData.data?.available || false
      setSlugAvailable(isAvailable)
      setSlugIssue(isAvailable ? null : "taken")
      setIsCheckingSlug(false)
    } else if (checkSlugRespStatus === FetchStatus.Error) {
      setSlugAvailable(null)
      setIsCheckingSlug(false)
    }
  }, [checkSlugRespStatus, checkSlugRespData])

  const handleSaveBasicInfo = () => {
    if (!editedName.trim() || !editedSlug.trim()) {
      alertToast({
        title: t("workspace.channels.messages.error.updateChannel.title"),
        description: t(
          "workspace.channels.messages.error.updateChannel.nameSlugRequired"
        ),
      })
      return
    }

    if (isReservedClientChannelSlug(editedSlug)) {
      setSlugIssue("reserved")
      alertToast({
        title: t("workspace.channels.messages.error.updateChannel.title"),
        description: t(
          "workspace.channels.messages.error.updateChannel.slugReserved"
        ),
      })
      return
    }

    // If slug changed, check if it's available
    if (editedSlug !== channel.slug && slugIssue === "reserved") {
      alertToast({
        title: t("workspace.channels.messages.error.updateChannel.title"),
        description: t(
          "workspace.channels.messages.error.updateChannel.slugReserved"
        ),
      })
      return
    }

    if (editedSlug !== channel.slug && slugAvailable === false) {
      alertToast({
        title: t("workspace.channels.messages.error.updateChannel.title"),
        description: t(
          "workspace.channels.messages.error.updateChannel.slugTaken"
        ),
      })
      return
    }

    updateChannel({
      jsonBody: makeUpdateClientChannelRequest(
        editedName.trim(),
        editedSlug.trim(),
        undefined
      ),
    })
  }

  // Handle update success
  useEffect(() => {
    if (!updateChannelRespData || !updateChannelRespData.data) return
    const updatedChannel = clientChannelResponseToClientChannel(
      updateChannelRespData.data
    )
    onChannelUpdated?.(updatedChannel)
    setIsEditingBasicInfo(false)
    successToast({
      title: t("workspace.channels.messages.updated.title"),
      description: t("workspace.channels.messages.updated.description"),
    })
  }, [updateChannelRespData, successToast, onChannelUpdated, t])

  // Handle update error
  useEffect(() => {
    if (!updateChannelRespError) return

    const normalizedError = normalizeApiError(updateChannelRespError)
    if (normalizedError.messageKey) {
      alertToast({
        title: t("workspace.channels.messages.error.updateChannel.title"),
        description: t(normalizedError.messageKey),
      })
      return
    }

    const nextSlugIssue = getClientChannelSlugIssue(
      editedSlug,
      updateChannelRespData?.message
    )
    if (nextSlugIssue) {
      setSlugIssue(nextSlugIssue)
      if (nextSlugIssue === "taken") {
        setSlugAvailable(false)
      }
      alertToast({
        title: t("workspace.channels.messages.error.updateChannel.title"),
        description: t(
          nextSlugIssue === "reserved"
            ? "workspace.channels.messages.error.updateChannel.slugReserved"
            : "workspace.channels.messages.error.updateChannel.slugTaken"
        ),
      })
      return
    }

    alertToast({
      title: t("workspace.channels.messages.error.updateChannel.title"),
      description: getUserFacingApiErrorMessage(
        updateChannelRespError,
        t,
        updateChannelRespData?.message ||
          t("workspace.channels.messages.error.updateChannel.description")
      ),
    })
  }, [updateChannelRespError, alertToast, editedSlug, t, updateChannelRespData])

  const handleCancelBasicInfo = () => {
    setEditedName(channel.name)
    setEditedSlug(channel.slug)
    setSlugAvailable(null)
    setSlugIssue(null)
  }

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {t("workspace.channels.overview.basicInfo.title")}
          </h3>
          <PermissionGuard
            permission={Permission.WsUpdateResources}
            workspace={workspace}
          >
            <EditSaveButtons
              size="sm"
              editText={t("workspace.channels.overview.actions.edit")}
              saveText={t("workspace.channels.overview.actions.save")}
              cancelText={t("workspace.channels.overview.actions.cancel")}
              isLoading={updateChannelRespStatus === FetchStatus.Loading}
              onSave={handleSaveBasicInfo}
              onCancel={handleCancelBasicInfo}
              onEditingChange={setIsEditingBasicInfo}
              disabled={
                isCheckingSlug ||
                (editedSlug !== channel.slug &&
                  (slugAvailable === false || slugIssue === "reserved")) ||
                updateChannelRespStatus === FetchStatus.Loading
              }
            />
          </PermissionGuard>
        </div>

        <Card className="p-6">
          <div className="space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">
                {t("workspace.channels.overview.basicInfo.name")}
              </Label>
              {isEditingBasicInfo ? (
                <Input
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder={t(
                    "workspace.channels.overview.basicInfo.namePlaceholder"
                  )}
                />
              ) : (
                <p className="text-sm">{channel.name}</p>
              )}
            </div>

            {/* Slug */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">
                {t("workspace.channels.overview.basicInfo.slug")}
              </Label>
              {isEditingBasicInfo ? (
                <>
                  <div className="relative">
                    <Input
                      value={editedSlug}
                      onChange={(e) =>
                        setEditedSlug(
                          e.target.value
                            .toLowerCase()
                            .replace(/\s+/g, "-")
                            .replace(/[^a-z0-9-]/g, "")
                        )
                      }
                      className="font-mono pr-10"
                      placeholder={t(
                        "workspace.channels.overview.basicInfo.slugPlaceholder"
                      )}
                    />
                    {editedSlug !== channel.slug && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {isCheckingSlug ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : slugAvailable === true ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : slugAvailable === false ||
                          slugIssue === "reserved" ? (
                          <X className="h-4 w-4 text-red-500" />
                        ) : null}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("workspace.channels.overview.basicInfo.slugDescription")}
                    {editedSlug !== channel.slug && slugIssue && (
                      <span className="text-red-500 ml-1">
                        {getSlugErrorMessage(slugIssue)}
                      </span>
                    )}
                  </p>
                </>
              ) : (
                <p className="text-sm font-mono">{channel.slug}</p>
              )}
            </div>

            {/* Timestamps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">
                  {t("workspace.channels.overview.timestamps.createdAt")}
                </Label>
                <p className="text-sm">
                  {format(channel.createdAt, "yyyy-MM-dd HH:mm:ss")}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">
                  {t("workspace.channels.overview.timestamps.lastUpdated")}
                </Label>
                <p className="text-sm">
                  {format(channel.updatedAt, "yyyy-MM-dd HH:mm:ss")}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Publish Status */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">
          {t("workspace.channels.overview.publishStatus.title")}
        </h3>
        <Card className="p-6">
          <div className="space-y-4">
            {/* Status */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">
                {t("workspace.channels.overview.publishStatus.status")}
              </Label>
              <div className="flex items-center">
                {channel.isPublished ? (
                  <>
                    {channel.isHealthy ? (
                      <Cloud className="w-4 h-4 inline-block mr-2 text-green-500" />
                    ) : (
                      <CloudOff className="w-4 h-4 inline-block mr-2 text-red-500" />
                    )}
                    <p className="text-sm">
                      {t("workspace.channels.overview.publishStatus.running")}
                    </p>
                  </>
                ) : (
                  <>
                    <CloudOff className="w-4 h-4 inline-block mr-2 text-red-500" />
                    <p className="text-sm">
                      {t(
                        "workspace.channels.overview.publishStatus.notPublished"
                      )}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* URL */}
            {isWebClientConfig(channel.config) && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">
                  {t("workspace.channels.overview.publishStatus.url")}
                </Label>
                <p className="text-sm">
                  <span className="flex items-center">
                    <a
                      href={channel.publishUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {channel.publishUrl || "-"}
                    </a>
                    <CopyButton content={channel.publishUrl} />
                  </span>
                </p>
                {/* TODO: Remove once feature confirmed done */}
                {showOauthDevTools && (
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const returnTo = window.location.origin
                        const loginUrl = `${serverRoot}/api/v1/web-auth/login?provider=google&slug=${encodeURIComponent(
                          channel.slug
                        )}&return_to=${encodeURIComponent(returnTo)}`
                        window.location.href = loginUrl
                      }}
                    >
                      Test OAuth Login (Dev Only)
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Version & Last Updated */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">
                  {t("workspace.channels.overview.basicInfo.version")}
                </Label>
                <p className="text-sm font-mono">
                  {channel.lastPublishedVersion || "-"}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">
                  {t("workspace.channels.overview.publishStatus.lastUpdated")}
                </Label>
                <p className="text-sm">
                  {channel.publishedAt
                    ? format(channel.publishedAt, "yyyy-MM-dd HH:mm:ss")
                    : "-"}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {isWidgetClientConfig(channel.config) && (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold">
            {t("workspace.channels.overview.embedSnippet.title")}
          </h3>
          <Card className="space-y-4 gap-1 p-6">
            <p className="mb-2 text-sm text-muted-foreground">
              {channel.isPublished
                ? t("workspace.channels.overview.embedSnippet.description")
                : t("workspace.channels.overview.embedSnippet.publishRequired")}
            </p>

            <div className="space-y-4">
              <div className="rounded-md bg-muted p-4">
                <div className="mb-2 flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-muted-foreground">
                      {t("workspace.channels.overview.embedSnippet.autoInit")}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "workspace.channels.overview.embedSnippet.autoInitDescription"
                      )}
                    </p>
                  </div>
                  <CopyButton content={widgetAutoInitSnippet} />
                </div>
                <pre className="overflow-x-auto whitespace-pre-wrap text-xs">
                  {widgetAutoInitSnippet}
                </pre>
              </div>

              <div className="rounded-md bg-muted p-4">
                <div className="mb-2 flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium text-muted-foreground">
                      {t("workspace.channels.overview.embedSnippet.manualInit")}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "workspace.channels.overview.embedSnippet.manualInitDescription"
                      )}
                    </p>
                  </div>
                  <CopyButton content={widgetManualInitSnippet} />
                </div>
                <pre className="overflow-x-auto whitespace-pre-wrap text-xs">
                  {widgetManualInitSnippet}
                </pre>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("workspace.channels.overview.embedSnippet.slugWarning")}
            </p>
          </Card>
        </div>
      )}
    </div>
  )
}

export default ChannelOverview
